import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { ListaTarjetasSkeleton, MobileOnly, PaginadorTabla, Stack } from "@/components/ui";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import type { ProveedorAdmin } from "@/lib/types";
import { useProveedoresAdmin } from "../hooks/use-proveedores-admin";
import { listarProveedoresAdmin } from "../services/proveedores-service";
import { ProveedoresHeader } from "./ProveedoresHeader";
import { ProveedoresFiltros } from "./ProveedoresFiltros";
import { ProveedorCrearForm } from "./ProveedorCrearForm";
import { SeleccionarTipoProveedorModal, type TipoProveedor } from "./SeleccionarTipoProveedorModal";
import { ProveedoresDesktopTable } from "./ProveedoresDesktopTable";
import { ProveedoresMobileList } from "./ProveedoresMobileList";

const PAGE_SIZE = 15;

/**
 * Página de proveedores · listado + filtros + form de creación.
 * Filtros (texto + solo activos) y paginado se resuelven en el SERVIDOR
 * (query params con debounce de 350ms) · el cliente solo pinta la página.
 * Dos presentaciones (desktop table / mobile cards).
 */
export function AdminProveedoresPage() {
  const [filtro, setFiltro] = useState("");
  const [soloActivos, setSoloActivos] = useState(false);
  // Alta en dos pasos: primero el modal elige el tipo, luego se muestra el form
  // con los campos/validaciones de ese tipo. `tipoCrear === null` = sin alta en curso.
  const [modalTipoAbierto, setModalTipoAbierto] = useState(false);
  const [tipoCrear, setTipoCrear] = useState<TipoProveedor | null>(null);
  // Prellenado desde la bandeja «Solicitudes de catálogos»: el RFC viene del CFDI (proveedor
  // nacional), así que se abre directo el alta nacional con RFC y razón social ya escritos.
  const { altaRfc, altaRazonSocial } = useSearch({ from: "/_authed-tw/admin/proveedores" });
  const navigate = useNavigate();
  const [prellenado, setPrellenado] = useState<{ rfc: string; razonSocial: string } | null>(null);
  useEffect(() => {
    if (!altaRfc) return;
    setPrellenado({ rfc: altaRfc, razonSocial: altaRazonSocial ?? "" });
    setTipoCrear("nacional");
    // Se limpia la URL para que recargar/volver no reabra el alta.
    void navigate({ to: "/admin/proveedores", search: {}, replace: true });
  }, [altaRfc, altaRazonSocial, navigate]);
  const [page, setPage] = useState(1);
  const [filtroDebounced] = useDebouncedValue(filtro, 350);

  const filtrosServer = useMemo(
    () => ({
      ...(filtroDebounced.trim() ? { busqueda: filtroDebounced.trim() } : {}),
      ...(soloActivos ? { soloActivos: true } : {}),
    }),
    [filtroDebounced, soloActivos]
  );

  useEffect(() => {
    setPage(1);
  }, [filtroDebounced, soloActivos]);

  const { data } = useProveedoresAdmin({ ...filtrosServer, page, pageSize: PAGE_SIZE });
  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const obtenerTodos = async (): Promise<ProveedorAdmin[]> => {
    const todos: ProveedorAdmin[] = [];
    for (let p = 1; ; p++) {
      const pagina = await listarProveedoresAdmin({ ...filtrosServer, page: p, pageSize: 100 });
      todos.push(...pagina.items);
      if (todos.length >= pagina.total || pagina.items.length === 0) break;
    }
    return todos;
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
      <ProveedoresHeader
        total={data?.totalCatalogo}
        activos={data?.totalActivos}
        modoCrear={tipoCrear !== null}
        onIniciarCrear={() => setModalTipoAbierto(true)}
      />
      <SeleccionarTipoProveedorModal
        open={modalTipoAbierto}
        onOpenChange={setModalTipoAbierto}
        onSeleccionar={(tipo) => {
          setTipoCrear(tipo);
          setModalTipoAbierto(false);
        }}
      />
      <Stack gap="md">
        {tipoCrear !== null ? (
          <ProveedorCrearForm
            key={prellenado ? `alta-${prellenado.rfc}` : "alta"}
            numeroSugerido={data?.siguienteNumero ?? 1}
            tipo={tipoCrear}
            rfcInicial={prellenado?.rfc}
            razonSocialInicial={prellenado?.razonSocial}
            onSalir={() => {
              setTipoCrear(null);
              setPrellenado(null);
            }}
            onCambiarTipo={() => setModalTipoAbierto(true)}
          />
        ) : null}
        <ProveedoresFiltros texto={filtro} onTextoChange={setFiltro} soloActivos={soloActivos} onSoloActivosChange={setSoloActivos} />
        <PaginadorTabla cargando={!data} total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} variante="superior" />
        <ProveedoresDesktopTable cargando={!data} items={items} obtenerFilas={obtenerTodos} />
        {data ? (
          <ProveedoresMobileList items={items} />
        ) : (
          <MobileOnly>
            <ListaTarjetasSkeleton />
          </MobileOnly>
        )}
        <PaginadorTabla cargando={!data} total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} sustantivo="proveedores" />
      </Stack>
    </div>
  );
}
